import React, { useState, Fragment } from "react";
import { Link, withRouter, RouteComponentProps } from "react-router-dom";
import { UtilityFunctions } from "../../utils/UtilityFunctions";
import { STRING_CONSTANTS } from "../../utils/constants/stringConstants";
import { Helmet } from 'react-helmet';
import { userService, cookieService } from "../../_services";
import "./ARAffSignup.css";

// Define props type including router props
interface ARAffSignupProps extends RouteComponentProps<any> {
  nextButton: () => void;
}

const ARAffSignup: React.FC<ARAffSignupProps> = (props) => {
  // --- State Management ---
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [affiliateCode, setAffiliateCode] = useState(localStorage.getItem("referralCode") || '');
  const [pwdType, setPwdType] = useState("password");
  const [btnClick, setBtnClick] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [mobileNumber, setMobileNumber] = useState('');
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);

  // --- Validation ---
  const hasNumber = /\d/;
  const isEmailValid = UtilityFunctions.validateEmail(email);
  const isPasswordValid = password.length >= 8 && hasNumber.test(password) && !UtilityFunctions.checkSpaceInPassword(password);
  const isMobileValid = !UtilityFunctions.isEmptyOrNullOrUndefined(mobileNumber);
  const isFormValid = isEmailValid && isPasswordValid && isMobileValid;

  // --- Handlers ---
  const togglePassword = () => {
    setPwdType(pwdType === "password" ? "text" : "password");
  };

  const scrollToForm = () => {
    document.getElementById('ar-signup-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePlayVideo = () => {
    setShowVideoPlayer(true);
  };

  const handleAffiliateSignup = () => {
    props.history.push('/accounts/affiliate');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBtnClick(true);
    setApiError(null);

    if (!isFormValid) {
      console.log("Validation failed, not submitting.");
      return;
    }

    setIsLoading(true);
    try {
      console.log("Attempting AR Signup with:", { email, password, mobileNumber, affiliateCode });

      const signupData = {
        email,
        password,
        mobileNumber,
        type: 'Mploi-er',
      };

      const response = await userService.signup(signupData, affiliateCode, true);

      const loginData = {
        email: email,
        password: password
      };

      const loginResponse = await userService.login(loginData);

      if (loginResponse.data && loginResponse.data.user && loginResponse.data.user.token) {
        localStorage.setItem('access_token', loginResponse.data.user.token.access);
        localStorage.setItem('refresh_token', loginResponse.data.user.token.refresh);
        localStorage.setItem('user_data', JSON.stringify(loginResponse.data.user));
        localStorage.setItem('isGuest', 'false');

        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.token, loginResponse.data.user.token.access);
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, loginResponse.data.user);
        cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.isGuest, false);

        await userService.getUserData();

        props.history.push('/ar');
      }

    } catch (error: any) {
      console.error("API Call Failed:", error);

      if (error && Array.isArray(error) && error[0] === 'Email') {
        setApiError("Email already exists");
      } else {
        setApiError(
          error.response?.data?.message ||
          error.message ||
          "An unexpected error occurred during signup."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const terms = { pathname: `/terms`, state: {} };
  const privacy = { pathname: `/privacy`, state: {} };

  const benefits = [
    {
      title: "Your AI-Powered Invoice Follow-ups",
      text: "No more uncomfortable conversations asking customers to pay outstanding invoices. Let our AI handle it while you focus on running your business."
    },
    {
      title: "Seamless Software Integration",
      text: "MPLOI integrates with all your favorite accounting software including Xero and QuickBooks, or simply upload an Excel spreadsheet of your outstanding debtors."
    },
    {
      title: "Customizable AI Team Member",
      text: "Choose from multiple voices and tonalities for your AI Team Member, ensuring the best possible customer experience while collecting payments."
    },
    {
      title: "Personalized Mobile Number",
      text: "Get a dedicated mobile number for your business where customers can call back regarding their invoices."
    },
    {
      title: "Complete Call Tracking",
      text: "Every call is transcribed, logged, and emailed directly to you so you can see the outcome of each interaction."
    },
    {
      title: "Smart Follow-ups",
      text: "Your AI assistant remembers previous conversations with debtors and follows up appropriately, maintaining context across multiple interactions."
    }
  ];

  return (
    <Fragment>
      <Helmet>
        <title>MPLOI: Boost Your Cash Flow with AI-Powered Invoice Collection</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Urbanist:wght@400;500;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="ar-signup">
        {/* Navigation */}
        <nav className="ar-nav">
          <img
            src={UtilityFunctions.getImageURL() + "logo_White.svg"}
            alt="MPLOI Logo"
            className="ar-nav-logo"
            onClick={() => !isLoading && props.history.push("/")}
          />
          <ul className="ar-nav-links">
            <li><a href="https://mploi.com/#about">About Us</a></li>
            <li><a href="https://mploi.com/#services">Services</a></li>
            <li><a href="https://mploi.com/blog">Blog</a></li>
            <li><a href="https://mploi.com/#pricing">Pricing</a></li>
            <li><a href="https://mploi.com/#contact">Contact</a></li>
          </ul>
          <div className="ar-nav-actions">
            <button className="ar-btn-login" onClick={() => props.history.push('/signin')}>Log In</button>
            <button className="ar-btn-get-started" onClick={scrollToForm}>Get Started</button>
          </div>
        </nav>

        {/* Hero */}
        <section className="ar-hero">
          <div className="ar-hero-content">
            <h1>AI-Powered<br/>Invoice Collection</h1>
            <p className="ar-hero-tagline">Boost your cash flow. Let AI handle the follow-ups.</p>
            <p className="ar-hero-subtitle">
              Our intelligent AI system calls your customers with outstanding invoices,
              maintaining a professional tone, and facilitating immediate payments —
              all without the awkward conversations.
            </p>
            <button className="ar-hero-cta" onClick={scrollToForm}>
              Get Started <span style={{fontSize: '20px'}}>↗</span>
            </button>
          </div>
        </section>

        {/* Content */}
        <section className="ar-content">
          <div className="ar-content-inner">
            <span className="ar-section-label">About MPLOI AR</span>
            <h2>Stop chasing invoices. Start getting paid.</h2>
            <div className="ar-content-body">
              <p>
                Chasing outstanding invoices is one of the most time-consuming and uncomfortable
                parts of running a business. It strains relationships, drains resources,
                and takes your focus away from growth.
              </p>
              <p>
                <strong>MPLOI's AI-Powered Invoice Chaser changes everything.</strong>
              </p>
              <p>
                Our intelligent AI team member handles follow-up calls on your behalf —
                professionally, consistently, and without the awkwardness. It integrates
                seamlessly with your existing accounting software, remembers every conversation,
                and follows up at the right time with the right tone.
              </p>
              <p>
                Every interaction is transcribed, logged, and delivered straight to your inbox.
                You stay in control while your AI does the heavy lifting.
              </p>
              <p className="ar-closing">
                One platform. Smarter collections. Better cash flow — for everyone.
              </p>
            </div>
          </div>
        </section>

        {/* Video Section */}
        <section className="ar-video-section">
          <div className="ar-video-inner">
            <h2>See How It Works</h2>
            <p className="ar-video-desc">
              Watch our 2-minute demo to learn how MPLOI AR helps businesses recover outstanding invoices automatically.
            </p>

            <div className="ar-video-container">
              {!showVideoPlayer ? (
                <>
                  <img
                    src="https://res.cloudinary.com/dimqqmfx6/image/upload/v1743453131/placeholderm_gp8jmg.png"
                    alt="MPLOI AR Demo Video Thumbnail - Click to play"
                    className="ar-video-thumbnail"
                    onClick={handlePlayVideo}
                  />
                  <button
                    className="ar-play-btn"
                    onClick={handlePlayVideo}
                    aria-label="Play video"
                  >
                    <div className="ar-play-icon"></div>
                  </button>
                </>
              ) : (
                <div className="ar-video-embed">
                  <iframe
                    src="https://www.youtube.com/embed/psdciFVMNfY?autoplay=1&rel=0&modestbranding=1&showinfo=0"
                    title="MPLOI AR Demo Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="ar-features">
          <div className="ar-features-inner">
            <h2>Why Choose MPLOI Invoice Chaser?</h2>
            <div className="ar-feature-list">
              {benefits.map((benefit, index) => (
                <div key={index} className="ar-feature-item">
                  <div className="ar-feature-check">✓</div>
                  <div className="ar-feature-text">
                    <strong>{benefit.title}:</strong> {benefit.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Signup Form */}
        <section className="ar-form-section" id="ar-signup-form">
          <div className="ar-form-container">
            <h2 className="ar-form-title">Create Your MPLOI AR Account</h2>

            <form onSubmit={handleSubmit} noValidate>
              <div className="ar-form-group">
                <input
                  type="email"
                  placeholder="Email Address"
                  className={`ar-form-input ${btnClick && !isEmailValid ? 'error' : ''}`}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setBtnClick(false); setApiError(null); }}
                  disabled={isLoading}
                  required
                />
                {btnClick && !isEmailValid && <div className="ar-form-error">Please enter a valid email address</div>}
              </div>

              <div className="ar-form-group">
                <input
                  type="tel"
                  placeholder="Mobile Phone"
                  className={`ar-form-input ${btnClick && !isMobileValid ? 'error' : ''}`}
                  value={mobileNumber}
                  onChange={(e) => { setMobileNumber(e.target.value); setBtnClick(false); setApiError(null); }}
                  disabled={isLoading}
                  required
                />
                {btnClick && !isMobileValid && <div className="ar-form-error">Please enter your mobile number</div>}
              </div>

              <div className="ar-form-group">
                <input
                  type={pwdType}
                  placeholder="Password"
                  className={`ar-form-input ${btnClick && !isPasswordValid ? 'error' : ''}`}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setBtnClick(false); setApiError(null); }}
                  disabled={isLoading}
                  required
                />
                <div className="ar-form-icon" onClick={togglePassword}>
                  <img
                    src={pwdType === "password" ?
                      UtilityFunctions.getImageURL() + 'eyeOff.png' :
                      UtilityFunctions.getImageURL() + 'eyeOn.png'}
                    alt="Toggle password"
                  />
                </div>
                {btnClick && !isPasswordValid && <div className="ar-form-error">Password must meet the requirements below</div>}
              </div>

              <div className="ar-form-group">
                <input
                  type="text"
                  placeholder="Affiliate Code (Optional)"
                  className="ar-form-input"
                  value={affiliateCode}
                  onChange={(e) => setAffiliateCode(e.target.value)}
                  disabled={isLoading || !!localStorage.getItem("referralCode")}
                />
              </div>

              <ul className="ar-validation-list">
                <li className={`ar-validation-item ${password.length <= 0 ? '' : password.length >= 8 && !UtilityFunctions.checkSpaceInPassword(password) ? 'valid' : 'invalid'}`}>
                  ✓ {STRING_CONSTANTS.validationString.min_chars}
                </li>
                <li className={`ar-validation-item ${password.length <= 0 ? '' : hasNumber.test(password) ? 'valid' : 'invalid'}`}>
                  ✓ {STRING_CONSTANTS.validationString.number_req}
                </li>
              </ul>

              {apiError && <div className="ar-api-error">{apiError}</div>}

              <button
                type="submit"
                className="ar-submit-btn"
                disabled={!isFormValid || isLoading}
              >
                {isLoading ? (
                  <span><span className="ar-spinner"></span>Creating Account...</span>
                ) : "UNLOCK BETTER CASH FLOW NOW"}
              </button>

              <div className="ar-form-footer">
                <p>
                  By signing up, you agree to our{" "}
                  <Link to={terms}>Terms of Service</Link>{" "}and{" "}
                  <Link to={privacy}>Privacy Policy</Link>
                </p>
                <p style={{marginTop: '16px'}}>
                  Already have an account?{" "}
                  <Link to={STRING_CONSTANTS.navigationString.login}>Sign In</Link>
                </p>
                <p style={{marginTop: '8px'}}>
                  Want to become an affiliate?{" "}
                  <span onClick={handleAffiliateSignup}>Join our Affiliate Program</span>
                </p>
              </div>
            </form>
          </div>
        </section>

        {/* Footer */}
        <footer className="ar-footer">
          <p>Questions? Contact our support team</p>
          <p><a href="mailto:info@mploi.com">info@mploi.com</a></p>
          <p style={{marginTop: '16px'}}>
            <Link to={privacy}>Privacy Policy</Link>{" | "}
            <Link to={terms}>Terms & Conditions</Link>
          </p>
          <p style={{marginTop: '24px', fontSize: '12px'}}>
            All rights reserved. Copyright © {new Date().getFullYear()} MPLOI
          </p>
        </footer>
      </div>
    </Fragment>
  );
};

export default withRouter(ARAffSignup);
